// Blog Posts API Service
import { api } from "./api";

export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  meta_title: string;
  meta_description: string;
  author_id: number;
  author_name: string;
  category_id: number;
  category_name: string;
  category_slug: string;
  category_color: string;
  status: "draft" | "published" | "archived" | "scheduled";
  featured_image?: string;
  featured_image_alt?: string;
  is_featured: boolean;
  is_comments_enabled: boolean;
  views_count: number;
  likes_count: number;
  reading_time_minutes: number;
  published_at: string | null;
  scheduled_at: string | null;
  created_at: string;
  updated_at: string;
  tags?: BlogTag[];
}

export interface BlogCategory {
  id: number;
  name: string;
  slug: string;
  description?: string;
  color: string;
  icon?: string;
  sort_order: number;
  is_active: boolean;
}

export interface BlogTag {
  id: number;
  name: string;
  slug: string;
  color: string;
  usage_count: number;
}

export interface BlogPostFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  category?: string;
  author?: string;
  featured?: boolean;
  sort_by?: string;
  sort_order?: "ASC" | "DESC";
}

export interface BlogPostsResponse {
  posts: BlogPost[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface Author {
  id: number;
  username: string;
  email: string;
  name: string;
  post_count: number;
}

export const blogPostsService = {
  // Get blog posts with filters and pagination
  async getPosts(filters: BlogPostFilters = {}): Promise<BlogPostsResponse> {
    const params = new URLSearchParams();
    params.append("action", "list");

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    return api.get<BlogPostsResponse>(`/blog-posts.php?${params.toString()}`);
  },

  // Get single blog post by ID
  async getPost(id: number | string): Promise<BlogPost> {
    const response = await api.get<{ post: BlogPost }>(
      `/blog-posts.php?action=get&id=${id}`
    );
    return response.post;
  },

  // Create new blog post
  async createPost(data: any): Promise<{ id: number }> {
    return api.post<{ id: number }>("/blog-posts.php", data);
  },

  // Update existing blog post
  async updatePost(id: number | string, data: any): Promise<void> {
    await api.put(`/blog-posts.php?id=${id}`, data);
  },

  // Delete blog post
  async deletePost(id: number | string): Promise<void> {
    await api.delete(`/blog-posts.php?id=${id}`);
  },

  // Get all categories
  async getCategories(): Promise<BlogCategory[]> {
    const response = await api.get<{ categories: BlogCategory[] }>(
      "/blog-posts.php?action=categories"
    );
    return response.categories;
  },

  // Get all tags
  async getTags(limit?: number): Promise<BlogTag[]> {
    const params = limit ? `&limit=${limit}` : "";
    const response = await api.get<{ tags: BlogTag[] }>(
      `/blog-posts.php?action=tags${params}`
    );
    return response.tags;
  },

  // Get all authors
  async getAuthors(): Promise<Author[]> {
    const response = await api.get<{ authors: Author[] }>(
      "/blog-posts.php?action=authors"
    );
    return response.authors;
  },

  // Get blog statistics
  async getStats(): Promise<any> {
    const response = await api.get<{ stats: any }>(
      "/blog-posts.php?action=stats"
    );
    return response.stats;
  },
};
